/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-underscore-dangle */
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useDispatchToStore } from '@hooks/reduxHooks';
import { sendFreeVideoWatched, updateView } from '@actions/classes';
import { setupAnalytics } from '@events/sendAnalytics';
import { Platform, LearnerId, LearnerUserName } from '@events/eventProps';
import Button from '@base/Button/Button';
import H6 from '@base/H6/H6';
import H4 from '@base/H4/H4';
import Close from '@icon/Close';
import { Spacings } from '@styles/index';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { getIsLoggedIn, getMe } from '@selectors/auth';
import Dynamic from '@base/Dynamic/Dynamic';
import Player from './Player';

const YoutubePlayer = Dynamic(() => import('./YoutubePlayer'));

const Wrapper = styled.div`
  height: ${({ $fullHeight }) => ($fullHeight ? '100vh' : '423px')};
  background: var(--color-base-fill);
  position: relative;
  @media only screen and (max-width: 47.95em) {
    height: 203px;
  }
`;

const DownloadWrapper = styled.div`
  position: absolute;
  bottom: 20%;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  padding: ${Spacings.SPACING_2B};
  cursor: pointer;
  h6 {
    padding-left: ${Spacings.SPACING_2B};
  }
`;

const InnerDiv = styled.div`
  height: ${({ $fullHeight }) => ($fullHeight ? '100vh' : '423px')};
  background: var(--color-base-fill);
  @media only screen and (max-width: 47.95em) {
    height: 203px;
  }
`;

const LoginFrameWrapper = styled(InnerDiv)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  flex-flow: column;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background: var(--color-i-white-transparent);
`;

const LoginFrameWrapperText = styled(H4)`
  margin-bottom: ${Spacings.SPACING_5B};
`;

const FreeLessonFrame = ({ lessonDetails, user, isEmbed }) => {
  const dispatch = useDispatchToStore();
  const key =
    lessonDetails?.key ||
    lessonDetails?.distributionKey ||
    lessonDetails?.video?.key;
  const cdnUrl = lessonDetails?.cdnUrl || lessonDetails?.video?.cdnUrl;
  const videoType = lessonDetails?.videoType || lessonDetails?.video?.type;
  const youtubeId = lessonDetails?.youtubeId || lessonDetails?.video?.youtubeId;
  const strKey = `unacademy_player_async_${key}`;
  const [showDownload, setShowDownload] = useState(false);
  const { pathname } = useRouter();

  const me = useSelector(getMe);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  // Only prompt user to login in case they are on the free-lesson page
  const requiresLogin = pathname.includes('/lesson/[lessonSlug]/[lessonUID]');

  const updateViewCount = () => {
    dispatch(updateView(lessonDetails?.uid));
  };

  const videoWatched = () => {
    dispatch(sendFreeVideoWatched(lessonDetails?.uid));
  };

  const onVideoEnd = () => {
    // goto next lesson
  };

  const onPdfClick = () => {
    window.open(
      lessonDetails?.pdfUrl || lessonDetails?.video?.pdfUrl,
      '_blank'
    );
  };

  const actions = {
    updateViewCount,
    videoWatched,
    onVideoEnd
  };

  useEffect(() => {
    let timeout = null;
    let player = null;

    if (isLoggedIn) {
      player = new Player(lessonDetails, actions, user);
      window._unq = window._unq || [];
      player.setup();
      timeout = setTimeout(() => setShowDownload(true), 5000);
    }

    return () => {
      player?.unmount();
      clearTimeout(timeout);
    };
  }, [key, isLoggedIn, lessonDetails, user]);

  // Free Player Script
  useEffect(() => {
    setupAnalytics(me, {
      [Platform]: 'Desktop',
      [LearnerId]: me.uid,
      [LearnerUserName]: me.username
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
  return (
    <Wrapper $fullHeight={isEmbed}>
      {!isEmbed && showDownload ? (
        <DownloadWrapper>
          <Close
            size="16px"
            color="var(--color-base-1)"
            onClick={() => setShowDownload(false)}
          />
          <H6 color="var(--color-base-1)" onClick={onPdfClick}>
            Download PDF
          </H6>
        </DownloadWrapper>
      ) : null}
      {videoType === 2 ? (
        <YoutubePlayer videoId={youtubeId} />
      ) : (
        <>
          {!isLoggedIn && requiresLogin && (
            <LoginFrameWrapper>
              <LoginFrameWrapperText>
                Please Login To Continue
              </LoginFrameWrapperText>
              <Button color="green" onClick={openLoginDrawer} label="Login" />
            </LoginFrameWrapper>
          )}
          <InnerDiv
            className={strKey}
            data-url={cdnUrl}
            $fullHeight={isEmbed}
          />
        </>
      )}
    </Wrapper>
  );
};

export default FreeLessonFrame;

FreeLessonFrame.propTypes = {
  lessonDetails: PropTypes.objectOf(PropTypes.any).isRequired,
  user: PropTypes.objectOf(PropTypes.any).isRequired,
  isEmbed: PropTypes.bool
};

FreeLessonFrame.defaultProps = {
  isEmbed: false
};
